import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { viewBanner } from "../../utils/backend";
import { CTabs, CTabList, CTabContent, CTab, CTabPanel } from "@coreui/react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { CImage } from "@coreui/react-pro";
import { formatDateTime } from "../../components/FormattedTime/FormatDateTime";

const hasPermission = (permissionGroup, permissionName) => {
  let permissions;
  try {
    permissions = JSON.parse(localStorage.getItem("permissions")) || [];
  } catch (error) {
    console.error("Error parsing permissions from localStorage:", error);
    permissions = [];
  }

  return permissions.some(
    (perm) =>
      perm.permission_group === permissionGroup &&
      perm.permission_name === permissionName
  );
};

const ViewBanner = () => {
  const location = useLocation();
  const { totalBanners, usedSequences } = location.state || {};

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [banner, setBanner] = useState({});
  const { id } = useParams();

  useEffect(() => {
    fetchData(id);
  }, [id]);

  const fetchData = async (id) => {
    setIsLoading(true);
    try {
      const response = await viewBanner(id);
      setBanner(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setBanner({});
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate("/banners");
  };

  const RenderBannerDetails = ({ data }) => {
    const { title, image, sequence, status, created_at, updated_at, url_link, type } =
      data || {};

    return (
      <>
        <div className="d-flex justify-content-center mb-5">
          <a href={`${image}`} target="_blank" rel="noreferrer">
            <CImage
              rounded
              alt="banner-display"
              src={`${image}`}
              style={{ transition: "opacity 0.3s" }}
              className="hover-image banner-image"
              onMouseOver={(e) => (e.target.style.opacity = "0.7")}
              onMouseOut={(e) => (e.target.style.opacity = "1")}
            ></CImage>
          </a>
        </div>
        <div className="col-md-4 mb-3">
          <label className="form-label">Banner ID:</label>
          <p>
            <b>{id || "N/A"}</b>
          </p>
        </div>
        <div className="col-md-4 mb-3">
          <label className="form-label">Title:</label>
          <p>
            <b>{title || "N/A"}</b>
          </p>
        </div>
        <div className="col-md-4 mb-3">
          <label className="form-label">Sequence:</label>
          <p>
            <b>{sequence || "N/A"}</b>
          </p>
        </div>
        <div className="col-md-4 mb-3">
          <label className="form-label">Status:</label>
          <p>
            <b
              style={{
                backgroundColor: status === 1 ? "#66BB6A" : "#A9A9A9",
                color: "#fff",
                border: "none",
                width: "150px",
                borderRadius: "20px",
                fontWeight: "500",
                fontSize: "14px",
                display: "inline-block",
                textAlign: "center",
                padding: "5px 0",
              }}
            >
              {status === 1 ? "Active" : "Inactive"}
            </b>
          </p>
        </div>
        <div className="col-md-4 mb-3">
          <label className="form-label">Banner Created Date:</label>
          <p>
            <b>
              {created_at
                ? formatDateTime(created_at)
                : "N/A"}
            </b>
          </p>
        </div>
        <div className="col-md-4 mb-3">
          <label className="form-label">Banner Updated Date:</label>
          <p>
            <b>
              {updated_at
                ? formatDateTime(updated_at)
                : "N/A"}
            </b>
          </p>
        </div>
        <div className="col-md-4 mb-3">
          <label className="form-label">Image</label>
          <p>
            {image ? (
              <a href={`${image}`} target="_blank" rel="noreferrer">
                <b>{image.split("/").pop() || "N/A"}</b>
              </a>
            ) : (
              <strong>N/A</strong>
            )}
          </p>
        </div>
        <div className="col-md-4 mb-3">
          <label className="form-label">Type:</label>
          <p>
            <b>{type || "N/A"}</b>
          </p>
        </div>
        {type === "link" && (
          <div className="col-md-4 mb-3">
            <label className="form-label">Redirect URL:</label>
            <p>
              <b>{url_link || "N/A"}</b>
            </p>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      {isLoading ? (
        <div className="card border-light">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Skeleton
                height={40}
                width={100}
                style={{ marginRight: "10px" }}
              />
              <Skeleton height={40} width={100} />
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="card border-light">
            <div className="card-body p-4">
              <CTabs activeItemKey="banner-details">
                <CTabContent>
                  <CTabPanel className="p-3" itemKey="banner-details">
                    <div className="card-body p-4">
                      <div className="row">
                        <RenderBannerDetails data={banner} />
                      </div>
                    </div>
                  </CTabPanel>
                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn me-3 ansa-button-secondary"
                      onClick={handleBackClick}
                    >
                      Back
                    </button>
                    {hasPermission("Carousel Banner Management", "Edit") && (
                      <button
                        type="button"
                        className="btn ansa-button-primary"
                        onClick={() =>
                          navigate(`/banners/details/${id}/edit`, {
                            state: {
                              totalBanners: totalBanners,
                              usedSequences: usedSequences,
                            },
                          })
                        }
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </CTabContent>
              </CTabs>
            </div>
          </div>
        </>
      )}
      <style>
        {`
          .banner-image {
            max-height: 300px;
            object-fit: contain;
          }
          @media (max-width: 576px) {
            .banner-image {
              max-width: 100%;
              height: auto;
            }
          }
        `}
      </style>
    </>
  );
};

export default ViewBanner;
